/**
 * Save selected scene objects to the cloud library.
 *
 * This is the core save flow:
 * 1. Determine type (object vs collection) from selection
 * 2. Capture a viewport thumbnail
 * 3. Build a .pr v3 file
 * 4. Upload both to R2 via presigned URLs
 * 5. Create the library item record via API
 */

import type { LibraryItemType } from "@poseref/shared";
import * as THREE from "three/webgpu";
import { uploadToPresignedUrl } from "../api/client.ts";
import { updateItem } from "../api/library.ts";
import { checkQuota, getPresignedUploadUrl } from "../api/upload.ts";
import {
  isEmptyObject,
  isImagePlaneObject,
  isLightObject,
  isMannequinObject,
  type SceneObject,
} from "../modeling/types.ts";
import { useAuthStore } from "./authStore.ts";
import { getSceneObjectWorldTransform } from "./collectionGraph.ts";
import { importBlendToLibrary } from "./importToLibrary.ts";
import { buildCollectionFile, buildObjectFile } from "./libraryIO.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { selectionToLeafObjectIds } from "./objectSelection.ts";
import { captureSceneDocument } from "./persistence/sceneDocument.ts";
import { serializeSceneDocument } from "./persistence/sceneDocumentIO.ts";
import { useStore } from "./store.ts";
import { bakeTransformIntoVerticesPatch } from "./storeObjectOps.ts";
import { captureViewportThumbnail } from "./thumbnailCapture.ts";

/**
 * Track the library item ID of the currently open scene.
 * Persisted in sessionStorage so it survives HMR and page refresh.
 * Set when opening a scene from the library, cleared on new scene.
 */
const LIBRARY_ITEM_KEY = "poseref_current_library_item_id";
const PENDING_FOLDER_KEY = "poseref_pending_folder_id";

function readStoredItemId(): string | null {
  try {
    return sessionStorage.getItem(LIBRARY_ITEM_KEY);
  } catch {
    return null;
  }
}

let _currentLibraryItemId: string | null = readStoredItemId();

/**
 * Folder ID to assign when creating a new library item for a scene.
 * Set when "+ New Scene" is clicked while viewing a user folder.
 * Cleared after the first save or when opening an existing scene.
 */
let _pendingFolderId: string | null = (() => {
  try {
    return sessionStorage.getItem(PENDING_FOLDER_KEY);
  } catch {
    return null;
  }
})();

export function setCurrentLibraryItemId(id: string | null) {
  _currentLibraryItemId = id;
  try {
    if (id) sessionStorage.setItem(LIBRARY_ITEM_KEY, id);
    else sessionStorage.removeItem(LIBRARY_ITEM_KEY);
  } catch {}
}
export function getCurrentLibraryItemId() {
  return _currentLibraryItemId;
}

export function setPendingFolderId(id: string | null) {
  _pendingFolderId = id;
  try {
    if (id) sessionStorage.setItem(PENDING_FOLDER_KEY, id);
    else sessionStorage.removeItem(PENDING_FOLDER_KEY);
  } catch {}
}

/**
 * Bake each object's world transform into its vertex data and translate the
 * union so the asset's bounding-box center sits at the origin. Mirrors the
 * "Apply Transform → Center on Origin" workflow in Blender so the saved asset
 * spawns at the cursor in a predictable orientation, without inheriting the
 * scene-time pose of its source.
 *
 * Mannequins keep their world TRS — their vertices live in skin-local space
 * and can't be baked into the mesh. They get re-anchored via their position
 * field instead, so the relative arrangement of mixed selections is preserved.
 */
function applyAndCenterForLibrary(objects: SceneObject[]): SceneObject[] {
  const baked = objects.map((obj) => {
    if (isMannequinObject(obj) || obj.vertices.length === 0) return obj;
    const result = bakeTransformIntoVerticesPatch(obj, {
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
    });
    return result ? { ...obj, ...result.patch } : obj;
  });

  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  for (const obj of baked) {
    if (!isMannequinObject(obj) && obj.vertices.length > 0) {
      // Post-bake: vertices are in world space.
      for (let i = 0; i < obj.vertices.length; i += 3) {
        v.set(obj.vertices[i], obj.vertices[i + 1], obj.vertices[i + 2]);
        box.expandByPoint(v);
      }
    } else {
      // Mannequins / no-vert objects: TRS position is the only placement
      // signal we have without rigging in the skinning evaluator.
      v.fromArray(obj.position);
      box.expandByPoint(v);
    }
  }
  if (box.isEmpty()) return baked;

  const center = box.getCenter(new THREE.Vector3());
  const cx = center.x;
  const cy = center.y;
  const cz = center.z;

  return baked.map((obj) => {
    if (!isMannequinObject(obj) && obj.vertices.length > 0) {
      const newVerts = new Float32Array(obj.vertices);
      for (let i = 0; i < newVerts.length; i += 3) {
        newVerts[i] -= cx;
        newVerts[i + 1] -= cy;
        newVerts[i + 2] -= cz;
      }
      return { ...obj, vertices: newVerts };
    }
    return {
      ...obj,
      position: [
        obj.position[0] - cx,
        obj.position[1] - cy,
        obj.position[2] - cz,
      ] as [number, number, number],
    };
  });
}

/**
 * Save the current selection to the cloud library.
 *
 * The selection may be one object, several objects, or a mix of objects,
 * collections, and blend entries (resolved through `selectionToLeafObjectIds`).
 * A single saveable object becomes a `"object"` library item; anything more
 * becomes a `"collection"`. Lights and empties are filtered out — neither
 * round-trips cleanly through the object serializer, and silently saving
 * them would yield broken assets.
 *
 * Each object's world transform is baked into its vertex data and the union
 * is recentered on the origin (see `applyAndCenterForLibrary`), so the asset
 * spawns predictably at the cursor regardless of where it was authored.
 */
export async function saveToCloudLibrary(
  objectIds?: string[],
): Promise<boolean> {
  const state = useStore.getState();
  const ids = objectIds ?? [
    ...selectionToLeafObjectIds(
      state.blendFiles,
      state.collections,
      state.selectedObjectTargets,
    ),
  ];
  if (ids.length === 0) return false;

  // Resolve to live SceneObjects with world-space transforms. Lights and
  // empties are intentionally dropped: only geometry survives a save.
  const sourceObjects = ids.flatMap((id) => {
    const obj = state.sceneObjects.get(id);
    if (!obj) return [];
    // Image planes are local-only by design (CSAM concerns); they are
    // skipped here to keep the cloud library free of user-supplied imagery.
    if (isLightObject(obj) || isEmptyObject(obj) || isImagePlaneObject(obj))
      return [];
    const worldTransform = getSceneObjectWorldTransform(
      state.sceneObjects,
      state.collections,
      id,
    );
    if (!worldTransform) return [];
    return [
      {
        ...obj,
        position: [...worldTransform.position] as [number, number, number],
        rotation: [...worldTransform.rotation] as [number, number, number],
        scale: [...worldTransform.scale] as [number, number, number],
      },
    ];
  });

  if (sourceObjects.length === 0) {
    useStore.setState({
      error:
        "Save failed: selection contains no saveable geometry (lights and empties are skipped).",
    });
    return false;
  }

  const objects = applyAndCenterForLibrary(sourceObjects);

  const isSingle = objects.length === 1;
  const type: LibraryItemType = isSingle ? "object" : "collection";
  const name = isSingle
    ? objects[0].name
    : `${objects[0].name} +${objects.length - 1}`;

  try {
    const fileData = isSingle
      ? buildObjectFile(objects[0])
      : buildCollectionFile(objects);
    const fileBlob = new Blob([JSON.stringify(fileData)], {
      type: "application/json",
    });

    // Quota gate — throws (and shows a modal) when the user is over plan.
    await checkQuota(fileBlob.size);

    // Render the thumbnail with only this asset's objects (plus any lights)
    // visible, so the saved preview is uncluttered by the rest of the scene.
    const isolateObjectIds = new Set(objects.map((o) => o.id));
    const thumbnailBlob = await captureViewportThumbnail(256, isolateObjectIds);

    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/${type}s/${timestamp}_${rand}.pr`;
    const thumbnailKey = thumbnailBlob
      ? `library/thumbnails/${timestamp}_${rand}.webp`
      : undefined;

    const filePresign = await getPresignedUploadUrl(
      fileKey,
      "application/json",
    );
    await uploadToPresignedUrl(filePresign.url, fileBlob);

    if (thumbnailBlob && thumbnailKey) {
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/webp",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbnailBlob);
    }

    let vertexCount = 0;
    for (const obj of objects) vertexCount += obj.vertices.length / 3;

    await useLibraryStore.getState().createItem({
      name,
      type,
      fileKey,
      thumbnailKey,
      fileSize: fileBlob.size,
      metadata: {
        vertexCount,
        objectCount: objects.length,
        objectNames: objects.map((o) => o.name),
      },
    });

    useAuthStore.getState().refreshStorageUsage();
    return true;
  } catch (e) {
    console.error("Failed to save to library:", e);
    useStore.setState({
      error: `Save failed: ${e instanceof Error ? e.message : String(e)}`,
    });
    return false;
  }
}

/**
 * Save the entire current scene to the cloud library.
 * Called from the hamburger menu "Save to Library".
 */
export async function saveSceneToCloudLibrary(): Promise<boolean> {
  const state = useStore.getState();
  if (state.sceneSaving) return false;
  useStore.setState({ sceneSaving: true });

  // Derive a clean display name from the scene file name only.
  // Never fall back to the blend file name (state.fileName) — a scene's
  // identity is independent of which blend files it contains.
  const sceneName = (state.currentSceneFileName ?? "Untitled Scene.pr").replace(
    /\.pr$/i,
    "",
  );

  try {
    // 1. Capture the scene document and serialize it via the worker.
    const sceneData = captureSceneDocument(state);
    const json = await serializeSceneDocument(sceneData);
    const fileBlob = new Blob([json], { type: "application/json" });

    // 1b. Check storage quota before uploading (throws + shows modal on denial)
    await checkQuota(fileBlob.size);

    // 2. Capture viewport thumbnail
    const thumbnailBlob = await captureViewportThumbnail();

    // 3. Generate unique keys
    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/scenes/${timestamp}_${rand}.pr`;
    const thumbnailKey = thumbnailBlob
      ? `library/thumbnails/${timestamp}_${rand}.webp`
      : undefined;

    // 4. Upload file to R2
    const filePresign = await getPresignedUploadUrl(
      fileKey,
      "application/json",
    );
    await uploadToPresignedUrl(filePresign.url, fileBlob);

    // 5. Upload thumbnail
    if (thumbnailBlob && thumbnailKey) {
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/webp",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbnailBlob);
    }

    // 6. Ensure blend file exists as an independent library asset.
    //    The scene's .pr file references it by hash (blendFileRef); the blend
    //    item stores the actual .blend binary. No coupling on the scene item.
    for (const entry of state.blendFiles.values()) {
      const blendFile = new File([entry.originalBuffer], entry.fileName, {
        type: "application/octet-stream",
      });
      await importBlendToLibrary(blendFile, {
        buffer: entry.originalBuffer,
        hash: entry.hash,
      });
    }

    // 7. Create or update scene item — scenes never store sourceFileKey or
    //    sourceHash. The .pr file's blendFileRef is the sole reference to
    //    any blend assets; they are resolved by hash at load time.
    const objectCount = state.sceneObjects.size;
    const metadata = {
      objectCount,
      objectNames: [...state.sceneObjects.values()].map((o) => o.name),
    };

    if (_currentLibraryItemId) {
      const updated = await updateItem(_currentLibraryItemId, {
        name: sceneName,
        fileKey,
        thumbnailKey,
        fileSize: fileBlob.size,
        metadata,
      });
      useLibraryStore.setState((s) => ({
        items: s.items.map((i) => (i.id === updated.id ? updated : i)),
      }));
    } else {
      const item = await useLibraryStore.getState().createItem({
        name: sceneName,
        type: "scene",
        fileKey,
        thumbnailKey,
        fileSize: fileBlob.size,
        metadata,
        folderId: _pendingFolderId,
      });
      setCurrentLibraryItemId(item.id);
      setPendingFolderId(null);
    }

    // Mark scene as not dirty
    useStore.setState({ sceneDirty: false });
    useAuthStore.getState().refreshStorageUsage();

    return true;
  } catch (e) {
    console.error("Failed to save scene to library:", e);
    useStore.setState({
      error: `Save failed: ${e instanceof Error ? e.message : String(e)}`,
    });
    return false;
  } finally {
    useStore.setState({ sceneSaving: false });
  }
}
